// Library imports
import React from 'react';
import { MDBModal, MDBModalContent, MDBModalHeader, MDBModalTitle, MDBModalBody, MDBSpinner } from 'mdb-react-ui-kit';
import moment from 'moment';
import { shape, bool, func, object } from 'prop-types';
// Styled components
import {
    ModalBodyWrapper,
    LoadingContainer,
    ErrorContainer,
    NoDataContainer,
    ContentWrapper,
    ComparisonSection,
    DateTimeHeader,
    ComparisonCard,
    CardContent,
    ScoreComparisonRow,
    TeamColumn,
    TeamLabel,
    ScoreGroup,
    ScoreItem,
    ScoreLabel,
    ScoreValue,
    VsDivider,
    WinnerRow,
    WinnerSection,
    WinnerLabel,
    WinnerTeam,
    WinnerDivider,
    ConfidenceText,
    Divider,
    ExpandableSection,
    ExpandButton,
    ExpandIcon,
    DetailGrid,
    DetailCard,
    DetailLabel,
    DetailValue,
    CloseButton,
} from './styles';
import { GameDetailsModalDialog } from '../../styles';

const ScheduleGameModal = ({ isOpen, onClose, gameData, loading, error }) => {
    const [showDetails, setShowDetails] = React.useState(false);

    const renderLoadingState = () => (
        <LoadingContainer>
            <MDBSpinner />
            <p className="mt-2">Loading game details...</p>
        </LoadingContainer>
    );

    const renderErrorState = () => (
        <ErrorContainer className="text-danger">
            <p>{error}</p>
        </ErrorContainer>
    );

    const renderNoDataState = () => (
        <NoDataContainer>
            <p>No game data available</p>
        </NoDataContainer>
    );

    const renderComparison = () => {
        if (!gameData.prediction) return null;

        const awayScore = gameData.awayTeam?.score || 0;
        const homeScore = gameData.homeTeam?.score || 0;
        const hasActualScore = awayScore > 0 || homeScore > 0;

        const predictedWinner = gameData.prediction.winner === gameData.awayTeam?.teamId ? gameData.awayTeam?.abbr : gameData.homeTeam?.abbr;

        const actualWinner =
            gameData.winner === gameData.awayTeam?.teamId
                ? gameData.awayTeam?.abbr
                : gameData.winner === gameData.homeTeam?.teamId
                ? gameData.homeTeam?.abbr
                : null;

        return (
            <ComparisonSection>
                <DateTimeHeader>{moment(gameData.dateTime).format('MMMM D, YYYY • h:mm A')}</DateTimeHeader>
                <ComparisonCard>
                    <CardContent>
                        {/* Score Comparison */}
                        <ScoreComparisonRow>
                            <TeamColumn>
                                <TeamLabel>{gameData.awayTeam?.abbr}</TeamLabel>
                                <ScoreGroup>
                                    <ScoreItem>
                                        <ScoreLabel>Predicted</ScoreLabel>
                                        <ScoreValue $type="predicted">{gameData.prediction.awayScore?.toFixed(0) || '-'}</ScoreValue>
                                    </ScoreItem>
                                    {hasActualScore && (
                                        <ScoreItem>
                                            <ScoreLabel>Actual</ScoreLabel>
                                            <ScoreValue $type="actual">{awayScore}</ScoreValue>
                                        </ScoreItem>
                                    )}
                                </ScoreGroup>
                            </TeamColumn>

                            <VsDivider>vs</VsDivider>

                            <TeamColumn>
                                <TeamLabel>{gameData.homeTeam?.abbr}</TeamLabel>
                                <ScoreGroup>
                                    <ScoreItem>
                                        <ScoreLabel>Predicted</ScoreLabel>
                                        <ScoreValue $type="predicted">{gameData.prediction.homeScore?.toFixed(0) || '-'}</ScoreValue>
                                    </ScoreItem>
                                    {hasActualScore && (
                                        <ScoreItem>
                                            <ScoreLabel>Actual</ScoreLabel>
                                            <ScoreValue $type="actual">{homeScore}</ScoreValue>
                                        </ScoreItem>
                                    )}
                                </ScoreGroup>
                            </TeamColumn>
                        </ScoreComparisonRow>

                        {/* Winner Comparison */}
                        <WinnerRow>
                            <WinnerSection>
                                <WinnerLabel>Predicted Winner</WinnerLabel>
                                <WinnerTeam $type="predicted">{predictedWinner}</WinnerTeam>
                                <ConfidenceText>{(gameData.prediction.confidence * 100).toFixed(0)}% PW Rating</ConfidenceText>
                            </WinnerSection>
                            {actualWinner && (
                                <>
                                    <WinnerDivider />
                                    <WinnerSection>
                                        <WinnerLabel>Actual Winner</WinnerLabel>
                                        <WinnerTeam $type="actual" $isCorrect={predictedWinner === actualWinner}>
                                            {actualWinner}
                                        </WinnerTeam>
                                        {predictedWinner === actualWinner && <ConfidenceText style={{ color: '#28a745' }}>✓ Correct</ConfidenceText>}
                                        {predictedWinner !== actualWinner && <ConfidenceText style={{ color: '#dc3545' }}>✗ Incorrect</ConfidenceText>}
                                    </WinnerSection>
                                </>
                            )}
                        </WinnerRow>
                    </CardContent>
                </ComparisonCard>
            </ComparisonSection>
        );
    };

    const renderAdditionalDetails = () => {
        if (!showDetails) return null;

        const hasPitchers = gameData.awayTeam?.pitcher || gameData.homeTeam?.pitcher;
        const hasOdds = gameData.bookData?.moneylines?.length > 0 || gameData.bookData?.spreads?.length > 0;

        if (!hasPitchers && !hasOdds) return null;

        return (
            <>
                <Divider />
                <DetailGrid>
                    {hasPitchers && (
                        <>
                            {gameData.awayTeam?.pitcher && (
                                <DetailCard>
                                    <DetailLabel>Away Pitcher</DetailLabel>
                                    <DetailValue>
                                        {gameData.awayTeam.pitcher.firstName} {gameData.awayTeam.pitcher.lastName}
                                    </DetailValue>
                                    <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', fontSize: '0.875rem' }}>
                                        <div>
                                            <span style={{ color: '#6c757d' }}>ERA: </span>
                                            <strong>{gameData.awayTeam.pitcher.stats['Earned Run Average']}</strong>
                                        </div>
                                        <div>
                                            <span style={{ color: '#6c757d' }}>W-L: </span>
                                            <strong>{gameData.awayTeam.pitcher.stats['Wins-Losses']}</strong>
                                        </div>
                                    </div>
                                </DetailCard>
                            )}
                            {gameData.homeTeam?.pitcher && (
                                <DetailCard>
                                    <DetailLabel>Home Pitcher</DetailLabel>
                                    <DetailValue>
                                        {gameData.homeTeam.pitcher.firstName} {gameData.homeTeam.pitcher.lastName}
                                    </DetailValue>
                                    <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', fontSize: '0.875rem' }}>
                                        <div>
                                            <span style={{ color: '#6c757d' }}>ERA: </span>
                                            <strong>{gameData.homeTeam.pitcher.stats['Earned Run Average']}</strong>
                                        </div>
                                        <div>
                                            <span style={{ color: '#6c757d' }}>W-L: </span>
                                            <strong>{gameData.homeTeam.pitcher.stats['Wins-Losses']}</strong>
                                        </div>
                                    </div>
                                </DetailCard>
                            )}
                        </>
                    )}
                    {hasOdds && gameData.bookData.moneylines?.[0] && (
                        <DetailCard>
                            <DetailLabel>Moneyline ({gameData.bookData.moneylines[0].label})</DetailLabel>
                            <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '0.5rem' }}>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>{gameData.awayTeam?.abbr}</div>
                                    <DetailValue>
                                        {gameData.bookData.moneylines[0].awayLine > 0 ? '+' : ''}
                                        {gameData.bookData.moneylines[0].awayLine}
                                    </DetailValue>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>{gameData.homeTeam?.abbr}</div>
                                    <DetailValue>
                                        {gameData.bookData.moneylines[0].homeLine > 0 ? '+' : ''}
                                        {gameData.bookData.moneylines[0].homeLine}
                                    </DetailValue>
                                </div>
                            </div>
                        </DetailCard>
                    )}
                </DetailGrid>
            </>
        );
    };

    const renderGameContent = () => {
        if (loading) return renderLoadingState();
        if (error) return renderErrorState();
        if (!gameData) return renderNoDataState();

        const hasAdditionalDetails =
            gameData.awayTeam?.pitcher || gameData.homeTeam?.pitcher || gameData.bookData?.moneylines?.length > 0 || gameData.bookData?.spreads?.length > 0;
        return (
            <ContentWrapper>
                {renderComparison()}
                {hasAdditionalDetails && (
                    <ExpandableSection>
                        <ExpandButton onClick={() => setShowDetails(!showDetails)}>
                            <span>Additional Details</span>
                            <ExpandIcon $expanded={showDetails}>▼</ExpandIcon>
                        </ExpandButton>
                        {renderAdditionalDetails()}
                    </ExpandableSection>
                )}
            </ContentWrapper>
        );
    };

    return (
        <MDBModal open={isOpen} onClose={onClose} tabIndex="-1">
            <GameDetailsModalDialog size="lg" centered scrollable>
                <MDBModalContent>
                    <MDBModalHeader>
                        <MDBModalTitle>Game Details</MDBModalTitle>
                        <CloseButton onClick={onClose} type="button">
                            ✕
                        </CloseButton>
                    </MDBModalHeader>
                    <MDBModalBody>
                        <ModalBodyWrapper>{renderGameContent()}</ModalBodyWrapper>
                    </MDBModalBody>
                </MDBModalContent>
            </GameDetailsModalDialog>
        </MDBModal>
    );
};

ScheduleGameModal.propTypes = {
    isOpen: bool.isRequired,
    onClose: func.isRequired,
    gameData: object,
    loading: bool,
    error: shape({}),
};

ScheduleGameModal.defaultProps = {
    gameData: null,
    loading: false,
    error: null,
};

export default ScheduleGameModal;
